import { useState } from "react";
import { api, formatError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ClientPicker } from "./ClientPicker";
import { displayName } from "./clientConstants";

export function ClientMergeDialog({ client, onClose, onMerged }) {
  const [target, setTarget] = useState(null);
  const [merging, setMerging] = useState(false);
  const name = displayName(client);

  const confirmMerge = async () => {
    if (!target || merging) return;
    const targetName = displayName(target);
    if (!window.confirm(`Merge "${name}" into "${targetName}"? All of ${name}'s orders, invoices, deliveries, and notes move to ${targetName}, and "${name}" is archived as a duplicate. This cannot be undone automatically.`)) return;
    setMerging(true);
    try {
      const { data } = await api.post(`/admin/clients/${client.id}/merge-into`, { primary_client_id: target.id });
      toast.success(`Merged ${name} into ${targetName} — ${data.orders_moved} order${data.orders_moved === 1 ? "" : "s"} moved`);
      onMerged();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setMerging(false); }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && !merging && onClose()}>
      <DialogContent className="rounded-xl max-w-md" data-testid="client-merge-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-3xl font-normal">Merge duplicate client</DialogTitle>
          <DialogDescription>Fold "{name}" into another client. Their orders, invoices, deliveries, and notes move over, and this record is archived as a duplicate — nothing is deleted.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <label className="text-[11px] font-mono uppercase tracking-[0.1em] text-[#475467]">Merge "{name}" into</label>
          <ClientPicker selected={target} onSelect={setTarget} onClear={() => setTarget(null)} excludeIds={[client.id]} allowAddNew={false} excludeArchived />
        </div>
        <DialogFooter className="gap-2">
          <Button data-testid="client-merge-cancel" variant="ghost" disabled={merging} onClick={onClose}>Cancel</Button>
          <Button data-testid="client-merge-confirm" disabled={!target || merging} onClick={confirmMerge} className="rounded-xl bg-[#0C2347] hover:bg-[#123262] text-white">{merging ? "Merging…" : "Merge clients"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
